import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { userScopeCleared } from './scope'

/**
 * Состояние входа С ТОЧКИ ЗРЕНИЯ КЛИЕНТА.
 *
 * Здесь нет профиля пользователя: имя и роли — ответ сервера, они живут в кэше
 * `sessionMe`. Слайс хранит только то, чего в кэше нет: событие «сессия
 * кончилась» (401 пришёл на ЛЮБОЙ запрос) и ключ области — id пользователя, под
 * которым пишутся user-scoped записи хранилища. Токенов здесь нет и быть не
 * может: их держит HttpOnly-cookie Gateway (D-084).
 *
 * Срок жизни — `screen`: после F5 состояние входа заново спрашивается у Gateway.
 */
export type SessionStatus = 'unknown' | 'active' | 'expired' | 'denied' | 'signedOut'

export interface SessionState {
  status: SessionStatus
  /** Чья область открыта: ключ user-scoped хранилища. `null` — ещё неизвестно. */
  scopeUserId: string | null
}

const initialState: SessionState = { status: 'unknown', scopeUserId: null }

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    sessionStarted(state, action: PayloadAction<{ userId: string }>) {
      state.status = 'active'
      state.scopeUserId = action.payload.userId
    },
    /** 401 на любой запрос: сессии нет. Повтора не будет — нужен вход (D-085). */
    sessionExpired(state) {
      state.status = 'expired'
    },
    /** 403 на «кто я»: сессия жива, но ролей приложения нет. На вход НЕ уводить — петля. */
    sessionDenied(state) {
      state.status = 'denied'
    },
    /**
     * Человек вышел, браузер уходит на выход Gateway. Конечное состояние до
     * перезагрузки: «кто я» больше не спрашивается, область пользователя не
     * открывается заново — иначе перезапрос после сброса кэша успел бы вернуть
     * в хранилище только что стёртые записи.
     */
    sessionSignedOut(state) {
      state.status = 'signedOut'
      state.scopeUserId = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
  },
})

export const { sessionStarted, sessionExpired, sessionDenied, sessionSignedOut } = sessionSlice.actions
export const sessionReducer = sessionSlice.reducer

export const selectSessionStatus = (state: SessionState): SessionStatus => state.status
export const selectScopeUserId = (state: SessionState): string | null => state.scopeUserId
